import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

let sisDateId = 0;

@Component({
  selector: 'app-sis-date-field',
  templateUrl: './sis-date-field.component.html',
  styleUrls: ['./sis-date-field.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SisDateFieldComponent),
      multi: true
    }
  ],
  standalone: false
})
export class SisDateFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = 'dd/mm/aaaa';
  @Input() helper = '';
  @Input() error = '';
  @Input() loading = false;
  @Input() loadedKey: unknown = null;
  @Input() loadedMessage = '';
  @Input() loadFailed = false;
  @Input() showRetry = true;
  // Optional bounds forwarded to the material datepicker; disable out-of-range
  // days in the calendar (e.g. a birthday field caps `max` at yesterday).
  @Input() min: Date | null = null;
  @Input() max: Date | null = null;
  @Output() retryLoad = new EventEmitter<void>();

  readonly inputId = `sis-date-${++sisDateId}`;
  value = '';
  dateValue: Date | null = null;
  @Input() disabled = false;
  // Form-independent lock (see SisFieldComponent.locked): disables the date input
  // for interaction/visual while keeping its value in the reactive form.
  @Input() locked = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get describedBy(): string | null {
    const ids = [];
    if (this.helper || this.loadFailed) {
      ids.push(`${this.inputId}-helper`);
    }
    if (this.error) {
      ids.push(`${this.inputId}-error`);
    }
    return ids.length ? ids.join(' ') : null;
  }

  get helperText(): string {
    return this.error || this.helper;
  }

  get failedText(): string {
    return this.error || this.helper || 'No se pudo cargar';
  }

  get effectiveDisabled(): boolean {
    return this.disabled || this.loading;
  }

  get isLockedOrDisabled(): boolean {
    return this.effectiveDisabled || this.locked;
  }

  writeValue(value: unknown): void {
    this.value = value == null ? '' : String(value);
    this.dateValue = this.parseDate(this.value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.dateValue = this.parseDate(this.value);
    this.onChange(this.value);
  }

  onBlur(): void {
    this.onTouched();
  }

  onDateChange(value: Date | null): void {
    this.dateValue = value;
    this.value = this.formatDate(value);
    this.onChange(this.value);
    this.onTouched();
  }

  private parseDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const slashParts = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashParts) {
      const day = Number(slashParts[1]);
      const month = Number(slashParts[2]) - 1;
      const year = Number(slashParts[3]);
      const date = new Date(year, month, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const dashParts = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (dashParts) {
      const year = Number(dashParts[1]);
      const month = Number(dashParts[2]) - 1;
      const day = Number(dashParts[3]);
      const date = new Date(year, month, day);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatDate(value: Date | null): string {
    if (!value) {
      return '';
    }

    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${value.getFullYear()}`;
  }
}
